import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * The admin users list.
 *
 * Three columns the imported design draws are absent, deliberately:
 *
 * - **Email.** Consumer identity is a phone number; `User` has no email column.
 *   Only admin operators have one. The list shows the phone instead.
 * - **Adherence.** Computing it means aggregating a month of meal logs per
 *   user. Affordable for one user on the detail screen, not for a page of them.
 * - **Last active.** No such column exists, and deriving it needs a per-row max
 *   across meal logs, workout sessions and chat sessions.
 *
 * Adding the last two properly means a maintained `lastActiveAt` column and a
 * periodic adherence rollup. Both are real work and neither is invented here.
 */
export const ADMIN_USER_STATUSES = ['active', 'deactivated'];
/** Mirrors Prisma's PlanWeekStatus, lower-cased for the wire. */
export const PLAN_STATUSES = [
    'generating',
    'active',
    'superseded',
    'failed',
    /** Never activated — lost the activation race, or its context was passed. */
    'obsolete',
    /** Active but stale; a validated replacement is being generated. */
    'reconciling',
    /** Active with affected items withheld pending a safety decision. */
    'safety_hold',
];
/** Mirrors Prisma's DietType. `jain_vegan` is "Jain, no dairy"; `jain` includes dairy. */
export const DIET_TYPES = [
    'vegetarian',
    'eggetarian',
    'non_vegetarian',
    'vegan',
    'jain',
    'jain_vegan',
];
/** Mirrors Prisma's GoalType. */
export const GOAL_TYPES = [
    'lose_weight',
    'build_muscle',
    'get_toned',
    'lose_belly_fat',
    'stamina_energy',
    'manage_condition',
];
/** Computed per row by `calculateTargets`, which is a pure function — no I/O. */
export const nutritionTargetsSchema = z.object({
    kcal: z.number().int(),
    proteinG: z.number().int(),
    carbsG: z.number().int(),
    fatG: z.number().int(),
});
export const DEVICE_PLATFORMS = ['ios', 'android', 'web'];
export const adminUserListItemSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    /** Consumer accounts key on a phone number, not an email. */
    phone: z.string(),
    status: z.enum(ADMIN_USER_STATUSES),
    /** Null until the user finishes onboarding — most other fields are null too. */
    onboardedAt: z.string().nullable(),
    createdAt: z.string(),
    goals: z.array(z.enum(GOAL_TYPES)),
    diet: z.enum(DIET_TYPES).nullable(),
    /** Null when onboarding is incomplete, so there is no profile to compute from. */
    targets: nutritionTargetsSchema.nullable(),
    /** Status of the user's most recent plan week; null if none was ever generated. */
    planStatus: z.enum(PLAN_STATUSES).nullable(),
    entitlement: z.enum(['free', 'premium']),
    /**
     * Platform of the device this account was last seen on; null when no
     * device ever registered. iOS builds before 26.3.2 reported Android.
     */
    platform: z.enum(DEVICE_PLATFORMS).nullable(),
});
export const adminUserListQuerySchema = cursorPageQuerySchema.extend({
    /** Matches display name or phone. */
    search: z.string().trim().max(120).optional(),
    status: z.enum(ADMIN_USER_STATUSES).optional(),
    plan: z.enum(PLAN_STATUSES).optional(),
    diet: z.enum(DIET_TYPES).optional(),
    /**
     * Whether onboarding was ever finished.
     *
     * Its own filter rather than a plan-status value: an account that never
     * answered the questions has no plan at all, so it cannot be reached by
     * filtering on one, and "signed up and stopped" is the single biggest
     * cohort the console needs to be able to look at.
     */
    onboarded: z.enum(['yes', 'no']).optional(),
    /** Accounts whose most recently seen device runs this platform. */
    platform: z.enum(DEVICE_PLATFORMS).optional(),
    /**
     * Gym coaching now, meaning an active membership: `any` (coached at some
     * gym), `none` (coached nowhere), or a gym's id (coached at that gym, which
     * is the list the Gyms screen's "members" link opens).
     */
    gym: z.union([z.enum(['any', 'none']), z.string().uuid()]).optional(),
});
//# sourceMappingURL=users.js.map